/**
 * Keep only the core match title for UI surfaces.
 * Removes trailing metadata like ", from ...", competition segments, and extra commas.
 */
export function formatMatchNameForDisplay(raw: unknown): string {
  if (typeof raw !== "string") return "";
  let name = raw.trim();
  if (!name) return "";

  // Drop trailing ", from ..." fragments.
  name = name.replace(/\s*,\s*from\s+.+$/i, "");

  // Keep only the first comma-separated segment (usually "Team A v Team B").
  name = name.split(",")[0]?.trim() || "";

  return name;
}

const VS_SPLIT = /\s+vs\.?\s+|\s+v\s+/i;

function abbreviateTeamSide(side: string): string {
  const s = side.trim();
  if (!s) return "";
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  if (words.length === 1) {
    const w = words[0].replace(/[^a-zA-Z0-9]/g, "");
    if (w.length <= 4) return w.toUpperCase();
    return w.slice(0, 3).toUpperCase();
  }
  const initials = words
    .map((w) => {
      const core = w.replace(/[^a-zA-Z0-9]/g, "");
      return core[0] ?? "";
    })
    .filter(Boolean)
    .join("")
    .toUpperCase();
  if (initials.length >= 2) return initials.slice(0, 4);
  return s.slice(0, 4).toUpperCase();
}

/**
 * Short match line for dense UI (e.g. friends activity drawer).
 * "Royal Challengers Bangalore v Mumbai Indians" → "RCB v MI"; single-sided names truncated.
 */
export function formatMatchNameAbbreviated(raw: unknown): string {
  const trimmed = formatMatchNameForDisplay(raw);
  if (!trimmed) return "";
  const parts = trimmed.split(VS_SPLIT).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return `${abbreviateTeamSide(parts[0])} v ${abbreviateTeamSide(parts[parts.length - 1])}`;
  }
  if (trimmed.length > 26) return `${trimmed.slice(0, 23)}…`;
  return trimmed;
}

